import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { scrollToId } from "../lib/site";

const LINES = ["Fasnet Sky Fiber", "Koneksi Stabil,", "Tanpa Kuota FUP,", "Internet Untuk Semua."];

export default function Hero() {
  const ref = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section
      id="beranda"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-[72px]"
    >
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/4 h-[480px] w-[480px] rounded-full bg-[#1E5AA8]/30 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-[#F04E23]/20 blur-[140px]" />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-8 sm:pt-12 lg:grid-cols-12 lg:px-8">
        <div className="relative lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            data-testid="hero-badge"
            className="mb-8 font-mono text-[16.8px] tracking-[0.25em] text-cyan-400"
          >
            ISP RESMI KALIMANTAN BARAT
          </motion.p>

          <h1 className="font-display text-[2.7rem] font-extrabold leading-[1.05] tracking-tight sm:text-[3.375rem] lg:text-[4.05rem]">
            {LINES.map((line, i) => {
              const last = i === LINES.length - 1;
              return (
                <span key={line} className={`block pb-1 ${last ? "" : "overflow-hidden"}`}>
                  <motion.span
                    className={`block ${i === 0 ? "text-white" : last ? "sm:whitespace-nowrap" : "text-chameleon"}`}
                    initial={last ? { opacity: 0, y: 24 } : { y: "110%" }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {last ? (
                      <>
                        <span className="text-chameleon-alt relative inline-block">
                          Internet Untuk
                          <span
                            data-testid="hero-mascot-fibi-mobile"
                            className="fibi-stand absolute left-full top-full z-10 ml-1 block h-24 w-[69px] sm:hidden"
                          >
                            <img
                              src="/images/fibi.png"
                              alt="FIBI, maskot tupai Fasnet Fiber"
                              className="h-full w-auto drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]"
                            />
                            <span
                              className="fibi-eye"
                              style={{ left: "28.3%", top: "22.8%", width: "10.4%", height: "8%" }}
                            />
                            <span
                              className="fibi-eye"
                              style={{ left: "50.6%", top: "22.8%", width: "10.4%", height: "8%" }}
                            />
                            <span
                              data-testid="hero-fibi-bubble-mobile"
                              className="fibi-bubble-hero tail-right absolute right-full top-0 mr-2 block w-max max-w-[130px] rounded-2xl bg-white px-3 py-1.5 text-[11px] font-bold text-[#0F1A36] shadow-xl"
                            >
                              Hallo, aku FIBI
                            </span>
                          </span>
                        </span>{" "}
                        <span className="text-chameleon-alt relative inline-block">
                          Semua.
                          <span
                            data-testid="hero-mascot-fibi"
                            className="fibi-stand absolute bottom-full right-0 z-10 hidden h-24 sm:block sm:h-32 lg:h-40"
                          >
                            <img
                              src="/images/fibi.png"
                              alt="FIBI, maskot tupai Fasnet Fiber"
                              className="h-full w-auto drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]"
                            />
                            <span
                              className="fibi-eye"
                              style={{ left: "28.3%", top: "22.8%", width: "10.4%", height: "8%" }}
                            />
                            <span
                              className="fibi-eye"
                              style={{ left: "50.6%", top: "22.8%", width: "10.4%", height: "8%" }}
                            />
                            <span
                              data-testid="hero-fibi-bubble"
                              className="fibi-bubble-hero absolute bottom-[85%] left-1/3 block w-max max-w-[160px] rounded-2xl bg-white px-3.5 py-2 text-xs font-bold text-[#0F1A36] shadow-xl"
                            >
                              Hallo, aku FIBI
                            </span>
                          </span>
                        </span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              );
            })}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-7 max-w-2xl pr-20 text-[13.5px] leading-relaxed text-slate-300 sm:pr-0 sm:text-[15px]"
          >
            100% jaringan fiber optik, nikmati koneksi stabil di rumah Anda. Tanpa batas
            kuota (Uncapped FUP), promo biaya pemasangan, dan dukungan teknisi lokal
            Kalimantan Barat 24/7.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-register"
              onClick={() => scrollToId("pasang")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#F04E23] px-8 py-4 text-lg font-bold text-white transition-colors duration-200 hover:bg-[#FF6B35]"
            >
              Ajukan Pemasangan Baru
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-packages"
              onClick={() => scrollToId("paket")}
              className="inline-flex items-center gap-2 rounded-full border border-[#2563EB]/60 bg-[#2563EB]/10 px-8 py-4 text-lg font-bold text-blue-300 transition-colors duration-200 hover:bg-[#2563EB]/25"
            >
              Lihat Pilihan Paket
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-[#1E5AA8]/40 to-[#F04E23]/30 blur-2xl" />
            <button
              data-testid="hero-image-btn"
              onClick={() =>
                setLightbox({ src: "/images/hero-hq.webp", alt: "Kantor pusat Fasnet Fiber" })
              }
              className="block w-full cursor-zoom-in"
            >
              <img
                src="/images/hero-hq.webp"
                alt="Kantor pusat Fasnet Fiber"
                data-testid="hero-image"
                className="relative h-auto w-full rounded-[1.5rem] border border-white/10 transition-colors duration-300 hover:border-cyan-400/40"
              />
            </button>
            <div className="relative mt-3 grid grid-cols-3 gap-3 sm:gap-4">
              <button
                data-testid="hero-thumb-lobby"
                onClick={() =>
                  setLightbox({ src: "/images/hero-lobby.webp", alt: "Lobby & ruang NOC Fasnet Fiber" })
                }
                className="col-span-1 row-span-2 block cursor-zoom-in"
              >
                <motion.img
                  src="/images/hero-lobby.webp"
                  alt="Lobby & ruang NOC Fasnet Fiber"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9, duration: 0.9 }}
                  className="h-full w-full rounded-2xl border border-white/15 object-cover transition-colors duration-300 hover:border-cyan-400/40"
                />
              </button>
              <div className="col-span-2 grid grid-rows-2 gap-3 sm:gap-4">
                <button
                  data-testid="hero-thumb-noc"
                  onClick={() =>
                    setLightbox({ src: "/images/hero-noc.webp", alt: "Network Operations Center Fasnet Fiber" })
                  }
                  className="block cursor-zoom-in"
                >
                  <img
                    src="/images/hero-noc.webp"
                    alt="Network Operations Center Fasnet Fiber"
                    className="h-full w-full rounded-xl border border-white/15 object-cover transition-colors duration-300 hover:border-cyan-400/40"
                  />
                </button>
                <button
                  data-testid="hero-thumb-rapat"
                  onClick={() =>
                    setLightbox({ src: "/images/hero-rapat.webp", alt: "Ruang Rapat Pimpinan Fasnet Fiber" })
                  }
                  className="block cursor-zoom-in"
                >
                  <img
                    src="/images/hero-rapat.webp"
                    alt="Ruang Rapat Pimpinan Fasnet Fiber"
                    className="h-full w-full rounded-xl border border-white/15 object-cover transition-colors duration-300 hover:border-cyan-400/40"
                  />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050914]/95 p-4 backdrop-blur-md"
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Tutup"
              className="absolute right-5 top-5 rounded-full border border-white/20 bg-white/10 p-2.5 text-white transition-colors duration-200 hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.img
              key={lightbox.src}
              data-testid="lightbox-img"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-h-[88vh] max-w-[94vw] rounded-2xl border border-white/15 object-contain shadow-2xl"
            />
            <p
              data-testid="lightbox-caption"
              className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-xs text-slate-400"
            >
              {lightbox.alt}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
